import { Observable, of } from "rxjs"
import { filter, switchMap, catchError, takeUntil, delayWhen } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import { flatten } from "lodash"
import { Router } from "../../../server/router"
import type { EpicDependencies } from "../../rxjs/configureEpics"
import { AGENDA_MOUNTED, REFETCH_DATA } from "../../auth/actions"
import {
    CLOSE_INVENTORY_ITEM_FORM_CLICKED,
    CLOSE_INVENTORY_STOCKING_FORM_CLICKED,
    DELETE_INVENTORY_ITEM_CLICKED,
    DELETE_INVENTORY_ITEM_LOCATION_CLICKED,
    INVENTORY_ITEM_FORM_SUBMITTED,
    INVENTORY_STOCKING_MODAL_FORM_SUBMITTED,
    LOG_OF_CHANGES_LOCATION_CHANGED,
    logOfChangesLocationChanged,
} from "./actions"
import { composeInventoryItem, composeInventoryItemLocation } from "./utils"
import {
    createActionLoadingError,
    inventoryItemCreated,
    inventoryItemDeleted,
    inventoryItemLocationCreated,
    inventoryItemLocationDeleted,
    inventoryItemTransactionCreated,
    inventoryItemUpdated,
} from "../../data/actions"
import { getInventoryItemById } from "../../data/selectors"
import {
    areLocationsAuthDataLoaded,
    getHttpHeaders,
    getSelectedAccountId,
    getUserAccountId,
} from "../../auth/selectors"
import { getCurrentPath } from "../../router/selectors"
import { URL_CHANGED } from "../../router/actions"
import {
    FETCH_INVENTORY_LOG_OF_CHANGES_REQUESTED,
    fetchInventoryLogOfChangesRequested,
    fetchInventoryLogOfChangesSuccess,
} from "../../../api/actions"
import { createLoadingEnded, createLoadingStarted } from "../../filters/actions"
import type { InventoryLogResponseItem } from "../../../api/inventoryLogOfChanges"
import { SEND_OPERATIONS_FAILURE, SEND_OPERATIONS_SUCCESS } from "../../operations/actions"
import { logger } from "../../../utils/loggerWrap"
import { Action, StateObject } from "../../redux/types"
import { itemsAndCategoriesListRequested } from "../categories/actions"
import { getItemsAndCategoriesListLocationId, getItemsAndCategoriesListResponse } from "../categories/selectors"
import { MainMenuAgendaEnum } from "../../router/types"
import { LOAD_AUTH_LOCATIONS_SUCCESS } from "../../layouts/header/actions"
import { isInventoryAgendaOpened } from "../../data/selectors"

const shouldRequestItemsAndCategoriesList = (state: StateObject) =>
    !(
        getItemsAndCategoriesListLocationId(state) == null &&
        (getItemsAndCategoriesListResponse(state)?.data?.length ?? 0) > 0
    )

export const inventoryItemFormSubmittedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(INVENTORY_ITEM_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("inventoryItemFormSubmittedEpics - form submitted", {
                data: action.payload,
            })

            // New Item
            if (!action.payload.oldValue) {
                // Per every item
                const final = action.payload.itemIds.map(categoryId => {
                    const inventoryItem = composeInventoryItem(action.payload.newValue, deps, categoryId)
                    const inventoryItemLocations = action.payload.locationIds.map(locationId =>
                        composeInventoryItemLocation(null, locationId, inventoryItem.id, deps)
                    )
                    return [
                        inventoryItemCreated({ ...inventoryItem, inventoryItemLocations: [] }),
                        ...inventoryItemLocations.map(iil => inventoryItemLocationCreated(iil)),
                    ]
                })
                return flatten(final)
            } else {
                return [
                    inventoryItemUpdated(
                        composeInventoryItem(action.payload.newValue, deps, action.payload.newValue.categoryId)
                    ),
                ]
            }
        })
    )

export const inventoryAgendaMountedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(AGENDA_MOUNTED, REFETCH_DATA, LOAD_AUTH_LOCATIONS_SUCCESS),
        filter((action: Action) =>
            action.type === AGENDA_MOUNTED
                ? action.payload?.type === MainMenuAgendaEnum.Inventory
                : isInventoryAgendaOpened(deps.getState())
        ),
        filter(() => {
            const state = deps.getState()
            return !!getSelectedAccountId(state) && areLocationsAuthDataLoaded(state)
        }),
        filter(() => shouldRequestItemsAndCategoriesList(deps.getState())),
        switchMap(() => {
            return [itemsAndCategoriesListRequested(null)]
        })
    )

export const inventoryStockingModalFormSubmittedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(INVENTORY_STOCKING_MODAL_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("inventoryStockingModalFormSubmittedEpics - form submitted", {
                data: action.payload,
            })

            const inventoryItem = getInventoryItemById(deps.getState(), action.payload.inventoryItemId)

            if (!inventoryItem) {
                return []
            }

            const inventoryItemLocation = inventoryItem.inventoryItemLocations.find(
                iil => iil.locationId === action.payload.locationId
            )
            const userAccountId = getUserAccountId(deps.getState())

            // First create InventoryItemLocation, than update
            if (!inventoryItemLocation) {
                const newInvItemLocation = composeInventoryItemLocation(
                    null,
                    action.payload.locationId,
                    inventoryItem.id,
                    deps
                )
                return [
                    inventoryItemLocationCreated(newInvItemLocation),
                    inventoryItemTransactionCreated({
                        id: deps.idgen.next(),
                        inventoryItemId: inventoryItem.id,
                        inventoryItemLocationId: newInvItemLocation.id,
                        amount: action.payload.count,
                        createdUserAccountId: userAccountId || null,
                        type: "restock",
                    }),
                ]
            }

            return [
                inventoryItemTransactionCreated({
                    id: deps.idgen.next(),
                    inventoryItemId: inventoryItem.id,
                    inventoryItemLocationId: inventoryItemLocation.id,
                    amount: action.payload.count,
                    createdUserAccountId: userAccountId || null,
                    type: "restock",
                }),
            ]
        })
    )

export const deleteInvetoryItemClickedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELETE_INVENTORY_ITEM_CLICKED),
        switchMap((action: Action) => {
            logger.info("deleteInvetoryItemClickedEpics - form submitted", {
                data: action.payload,
            })

            return [inventoryItemDeleted(action.payload.id)]
        })
    )

export const deleteInventoryItemLocationClickedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELETE_INVENTORY_ITEM_LOCATION_CLICKED),
        switchMap((action: Action) => {
            logger.info("deleteInventoryItemLocationClickedEpics - form submitted", {
                data: action.payload,
            })

            return [inventoryItemLocationDeleted(action.payload.invItemLocationId, action.payload.invItemId)]
        })
    )

export const createInvetoryItemSubmittedRouterEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(INVENTORY_ITEM_FORM_SUBMITTED, CLOSE_INVENTORY_ITEM_FORM_CLICKED, DELETE_INVENTORY_ITEM_CLICKED),
        switchMap(() => {
            Router.push("/inventory")
            return []
        })
    )

export const closeInventoryStockingFormClickedRouterEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(CLOSE_INVENTORY_STOCKING_FORM_CLICKED, INVENTORY_STOCKING_MODAL_FORM_SUBMITTED),
        switchMap(() => {
            const currentUrlQuery = getCurrentPath(deps.getState()).query
            const inventoryItemId = currentUrlQuery.id

            if (!inventoryItemId) {
                return []
            }

            Router.push(`/inventory/edit/${inventoryItemId}`)
            return []
        })
    )

export const fetchLogOfChangesEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_INVENTORY_LOG_OF_CHANGES_REQUESTED),
        switchMap((action: Action) =>
            deps.api
                .getInventoryLogOfChanges(
                    getHttpHeaders(deps.getState()),
                    action.payload.inventoryItemId,
                    action.payload.locationId
                )
                .pipe(
                    switchMap(response => {
                        return [
                            fetchInventoryLogOfChangesSuccess(
                                response.map((r: InventoryLogResponseItem) => ({
                                    amount: r.amount,
                                    inventoryTransactionId: r.inventory_transaction_id,
                                    locationName: r.location_name,
                                    originTimestamp: r.origin_timestamp,
                                    userAccountName: r.user_account_name,
                                    unit: r.unit,
                                }))
                            ),
                            createLoadingEnded("inventory_log_of_changes"),
                        ]
                    }),
                    catchError(error => of(createActionLoadingError(error)))
                )
        )
    )

export const inventoryItemDetailOpenedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(URL_CHANGED),
        switchMap((action: Action) => {
            if (
                action.payload.query &&
                action.payload.query.agenda &&
                action.payload.query.agenda === "inventory" &&
                action.payload.query.id
            ) {
                return [
                    createLoadingStarted("inventory_log_of_changes"),
                    fetchInventoryLogOfChangesRequested(action.payload.query.id, null),
                ]
            }

            return []
        })
    )

export const refreshLogOfChangesEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(INVENTORY_STOCKING_MODAL_FORM_SUBMITTED),
        delayWhen(() => actions$.pipe(ofType(SEND_OPERATIONS_SUCCESS))),
        takeUntil(actions$.pipe(ofType(SEND_OPERATIONS_FAILURE))),
        switchMap(() => {
            const currentUrlQuery = getCurrentPath(deps.getState()).query
            const inventoryItemId = currentUrlQuery.id

            if (!inventoryItemId) {
                return []
            }

            return [logOfChangesLocationChanged(null, inventoryItemId)]
        })
    )

export const logOfChangesLocationChangedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOG_OF_CHANGES_LOCATION_CHANGED),
        switchMap((action: Action) => {
            return [
                createLoadingStarted("inventory_log_of_changes"),
                fetchInventoryLogOfChangesRequested(action.payload.inventoryItemId, action.payload.locationId),
            ]
        })
    )

export default [
    inventoryAgendaMountedEpics,
    logOfChangesLocationChangedEpic,
    refreshLogOfChangesEpics,
    fetchLogOfChangesEpics,
    inventoryItemDetailOpenedEpics,
    inventoryItemFormSubmittedEpics,
    deleteInventoryItemLocationClickedEpics,
    closeInventoryStockingFormClickedRouterEpics,
    inventoryStockingModalFormSubmittedEpics,
    deleteInvetoryItemClickedEpics,
    createInvetoryItemSubmittedRouterEpics,
]
